import { useEffect, useId, useRef } from "react";
import { ChevronRight } from "lucide-react";
import {
  PsvReviewOutcome,
  PsvReviewStatus,
} from "@api/shared-runtime/psv-review";
import { cn } from "@/lib/utils";
import { OutcomePill } from "@/features/credentials/components/primary-source-runs/source-result-row";
import { ItemDetails } from "@/features/credentials/components/primary-source-runs/run-report-dialog/item-details";
import {
  isOpenItem,
  itemCheckOutcome,
  itemTitle,
  licenseSubtitle,
  showsIssueCallout,
} from "@/features/credentials/components/primary-source-runs/run-report-dialog/item-status";
import type { ItemDecisions } from "@/features/credentials/components/primary-source-runs/run-report-dialog/use-item-decisions";
import type {
  ReportItem,
  RunReport,
  RunScope,
} from "@/features/credentials/components/primary-source-runs/types";
import { formatRunDate } from "../../../format-run-time";
import { itemSourceCheck, itemSourceName } from "./item-columns";
import { REPORT_TABLE_COLUMNS } from "./report-table-columns";
import { VerifiedBy } from "./verified-by";

function rowLabel({
  title,
  expanded,
  opensSection,
}: {
  title: string;
  expanded: boolean;
  opensSection: boolean;
}) {
  if (opensSection) {
    return `Open ${title}`;
  }
  return `${expanded ? "Hide" : "View"} details for ${title}`;
}

function rowTint(item: ReportItem) {
  if (item.decision?.status === PsvReviewStatus.Concern) {
    return "bg-red-50/60";
  }
  if (
    isOpenItem(item) &&
    (item.outcome === PsvReviewOutcome.Issues ||
      item.outcome === PsvReviewOutcome.NotAvailable)
  ) {
    return "bg-amber-50/50";
  }
  return undefined;
}

/** One item as a table row, with its evidence and decision controls in an
 * expandable panel beneath. With `onOpen` the row only links to the item's
 * section (the overview), so the report shows each item's detail in one
 * place. */
export function ReportTableRow({
  item,
  checks,
  runId,
  scope,
  expanded,
  canEdit,
  decisions,
  uploadFocusRequested,
  focusRequested,
  onToggle,
  onOpen,
  onFocused,
  onUploadFocused,
  onAccept,
  onConcern,
  onUndo,
}: {
  item: ReportItem;
  checks: RunReport["checks"];
  runId: string;
  scope: RunScope;
  expanded: boolean;
  canEdit: boolean;
  decisions: ItemDecisions;
  uploadFocusRequested: boolean;
  /** Bring this row into view, as the item just opened from the overview. */
  focusRequested: boolean;
  onToggle: () => void;
  onOpen?: () => void;
  onFocused: () => void;
  onUploadFocused: () => void;
  onAccept: () => void;
  onConcern: () => void;
  onUndo: () => void;
}) {
  const detailsId = useId();
  const statusId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const opensSection = onOpen !== undefined;
  const showDetails = expanded && !opensSection;
  useEffect(() => {
    if (!focusRequested) {
      return;
    }
    buttonRef.current?.scrollIntoView({ block: "start" });
    buttonRef.current?.focus({ preventScroll: true });
    onFocused();
  }, [focusRequested, onFocused]);
  const title = itemTitle({ item, checks });
  let subtitle = licenseSubtitle({ item, checks });
  // Expanded, the issue banner states the verdict's description right below
  // the row, so the row does not repeat it.
  if (!subtitle && !(showDetails && showsIssueCallout(item))) {
    subtitle = item.verdict?.description || null;
  }
  const decided =
    item.decision?.status === PsvReviewStatus.Accepted ||
    item.decision?.status === PsvReviewStatus.Concern;
  const verifiedOn = itemSourceCheck({ item, checks })?.collectedAt;
  return (
    <article
      aria-label={`${title} result`}
      data-item-key={item.key}
      className={cn("border-b last:border-b-0", rowTint(item))}
    >
      <button
        ref={buttonRef}
        type="button"
        data-item-toggle
        aria-expanded={opensSection ? undefined : expanded}
        aria-controls={opensSection ? undefined : detailsId}
        aria-describedby={statusId}
        aria-label={rowLabel({ title, expanded, opensSection })}
        className={cn(
          "grid w-full cursor-pointer items-center gap-x-4 gap-y-1.5 px-4 py-3 text-left transition-colors hover:bg-slate-50/80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
          REPORT_TABLE_COLUMNS,
        )}
        onClick={onOpen ?? onToggle}
      >
        <span className="min-w-0 space-y-0.5">
          <span
            className={cn(
              "block break-words text-sm",
              decided ? "font-medium text-muted-foreground" : "font-semibold",
            )}
          >
            {title}
          </span>
          {subtitle && (
            <span className="block text-xs leading-5 text-muted-foreground">
              {subtitle}
            </span>
          )}
        </span>
        <span className="justify-self-start">
          <OutcomePill
            outcome={itemCheckOutcome(item)}
            inProgressLabel={item.verdict?.label ?? "In progress"}
          />
        </span>
        <span className="text-sm">
          <span className="text-xs text-muted-foreground sm:hidden">
            Verified on{" "}
          </span>
          {formatRunDate(verifiedOn)}
        </span>
        <span>
          <VerifiedBy decision={item.decision} id={statusId} />
        </span>
        <span className="min-w-0 break-words text-sm text-muted-foreground">
          {itemSourceName({ item, checks })}
        </span>
        <ChevronRight
          className={cn(
            "hidden size-4 shrink-0 text-muted-foreground transition-transform sm:block",
            showDetails && "rotate-90",
          )}
          aria-hidden
        />
      </button>
      {showDetails && (
        <div className="border-t bg-background">
          <ItemDetails
            id={detailsId}
            item={item}
            title={title}
            checks={checks}
            runId={runId}
            scope={scope}
            canEdit={canEdit}
            decisions={decisions}
            uploadFocusRequested={uploadFocusRequested}
            onUploadFocused={onUploadFocused}
            onAccept={onAccept}
            onConcern={onConcern}
            onUndo={onUndo}
          />
        </div>
      )}
    </article>
  );
}
